import { useUserConfig } from "hooks/useUserConfig";
import { TransformedFeatureConfig, FeatureUIBehavior } from "types";
import * as FeatureFlags from "helpers/constants/filters";

export interface FeatureFlagResult {
  show: boolean;
  enabled: boolean;
  reason?: string;
  featureOptions?: Record<string, unknown>;
}

type FeatureFlagKeys = (typeof FeatureFlags)[keyof typeof FeatureFlags];
type RequiredFeatureOverrides = Record<FeatureFlagKeys, boolean>;

const SET_ALL_FF_TRUE = false;
const SET_ALL_FF_FALSE = false;

const LOCAL_FEATURE_OVERRIDES: RequiredFeatureOverrides = {
  [FeatureFlags.TEXTUAL_SEARCH_IMAGE]: true,
  [FeatureFlags.TEXTUAL_SEARCH_OBJECT]: true,
  [FeatureFlags.VISUAL_SEARCH]: true,
  [FeatureFlags.SHOW_DS_VERSION]: true,
  [FeatureFlags.ENABLE_DS_DUPLICATE]: true,
  [FeatureFlags.REGION_SELECTION_SIMILARITY]: true,
  [FeatureFlags.DATA_INGESTION_TAB_AVAILABLE]: true,
  [FeatureFlags.USAGE_REPORT]: true,
  [FeatureFlags.DELETE_DATASET]: true,
  [FeatureFlags.EXPLORATION_DEBUG_INFO]: true,
  [FeatureFlags.DATASET_SHARE]: true,
  [FeatureFlags.CVAT_INTEGRATION]: true,
  [FeatureFlags.ENRICHMENT_PREVIEWS]: true,
  [FeatureFlags.CLUSTER_BY_DUPLICATES]: true,
  [FeatureFlags.UNIQUENESS_SCORE]: true,
  [FeatureFlags.PROMPT_SUPPORT_ENABLED]: true,
  [FeatureFlags.MISLABEL_CONFIDENCE_FILTER_FOR_OBJECTS]: true,
  [FeatureFlags.MISLABEL_CONFIDENCE_FILTER_FOR_IMAGES]: true,
  [FeatureFlags.ENRICHMENT_ENRICH_DATASET]: true,
  [FeatureFlags.TEXTUAL_SEARCH_RELEVANCE_SCORE_OBJECTS]: true,
  [FeatureFlags.TEXTUAL_SEARCH_RELEVANCE_SCORE_IMAGES]: true,
  [FeatureFlags.OBJECT_SIZE_FILTER_ENABLED]: true,
  [FeatureFlags.FLYWHEEL]: true,
  [FeatureFlags.VL_CHAT]: true,
  [FeatureFlags.DATASETS_JOB_SETUP_RECIPE_FILTERS]: true,
  [FeatureFlags.TRAIN_WORTHY]: true,
  [FeatureFlags.TRAIN_MODEL]: true,
  [FeatureFlags.VQL_UPGRADE]: true,
  [FeatureFlags.VQL_EXCLUDE]: true,
  [FeatureFlags.DATASET_SNAPSHOTS]: true,
  [FeatureFlags.USER_NOTIFICATIONS]: true,
  [FeatureFlags.ADD_MEDIA_TAB]: true,
  [FeatureFlags.USER_MANAGEMENT]: true,
  [FeatureFlags.DATASET_SNAPSHOTS_CREATE]: true,
  [FeatureFlags.DATASET_SNAPSHOTS_RESTORE]: true,
  [FeatureFlags.CAPTIONS_FILTER]: true,
  [FeatureFlags.VL_CHAT_DEBUG_CONSOLE_ENABLED]: true,
  [FeatureFlags.MODELS_CATALOG]: true,
  [FeatureFlags.RUNTIME_CONFIG]: true,
  [FeatureFlags.DATASET_CREATION_V2]: true,
  [FeatureFlags.DATASET_INGESTION_ON_PREM]: true,
};

/**
 * Custom hook for managing feature flags for single dataset context.
 * @param featureKey - The feature key to check
 * @returns FeatureFlagResult with show, enabled, reason, and featureOptions properties
 */
export function useFeatureFlagService(
  featureKey: FeatureFlagKeys,
): FeatureFlagResult {
  const userConfig = useUserConfig();

  // Get backend enabled value
  const config: TransformedFeatureConfig | undefined = userConfig?.config?.find(
    (cfg) => cfg.feature_key === featureKey,
  );
  let backendEnabled = false;
  if (config) {
    const behavior: FeatureUIBehavior | null | undefined =
      config.feature_behavior["ANY"];
    if (behavior) {
      backendEnabled = behavior === "SHOW";
    }
  }

  if (
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost"
  ) {
    if (SET_ALL_FF_TRUE) {
      return {
        enabled: true,
        show: true,
        featureOptions: config?.featureOptions,
      };
    }

    if (SET_ALL_FF_FALSE) {
      return {
        enabled: false,
        show: false,
        featureOptions: config?.featureOptions,
      };
    }

    if (featureKey in LOCAL_FEATURE_OVERRIDES) {
      return {
        enabled: backendEnabled, // Use backend enabled value
        show: LOCAL_FEATURE_OVERRIDES[featureKey as FeatureFlagKeys], // Use local show value (now just boolean)
        featureOptions: config?.featureOptions,
      };
    }
  }

  // Fallback to backend config for production
  if (config) {
    const behavior: FeatureUIBehavior | null | undefined =
      config.feature_behavior["ANY"];
    if (behavior) {
      return {
        reason: config.reason,
        enabled: behavior === "SHOW",
        show: behavior === "SHOW" || behavior === "GREY_OUT",
        featureOptions: config?.featureOptions,
      };
    }
  }

  return { enabled: false, show: false };
}
